import { useState } from "react";
import { Reveal, RevealGroup, RevealItem } from "./Reveal";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { REVIEWS } from "../../data/content";

const Stars = ({ n }) => (
  <div className="flex gap-0.5" aria-label={n + " z 5 hvězdiček"}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={
          "h-4 w-4 " +
          (i < n ? "fill-brand-orange text-brand-orange" : "text-slate-300")
        }
      />
    ))}
  </div>
);

const GoogleBadge = () => (
  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
    </svg>
    Google recenze
  </span>
);

const ReviewCard = ({ review }) => (
  <article
    data-testid={"review-card-" + review.name}
    className="h-full rounded-2xl bg-white border border-slate-200/80 p-6 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_-16px_rgba(10,25,47,0.2)] transition-all duration-300 flex flex-col"
  >
    <Stars n={review.stars} />
    <p className="mt-4 text-[15px] text-slate-700 leading-relaxed flex-1">
      „{review.text}&ldquo;
    </p>
    <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-navy text-white text-sm font-bold">
          {review.name.charAt(0)}
        </span>
        <span className="font-bold text-brand-navy text-sm">
          {review.name}
        </span>
      </div>
    </div>
    <div className="mt-3">
      <GoogleBadge />
    </div>
  </article>
);

export const Reviews = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const activeReview = REVIEWS[activeIndex];
  const swipeThreshold = 50;

  const goPrev = () => {
    setActiveIndex((current) =>
      current === 0 ? REVIEWS.length - 1 : current - 1
    );
  };

  const goNext = () => {
    setActiveIndex((current) =>
      current === REVIEWS.length - 1 ? 0 : current + 1
    );
  };

  const handleTouchStart = (event) => {
    setTouchStartX(event.touches[0].clientX);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX === null) return;

    const touchEndX = event.changedTouches[0].clientX;
    const swipeDistance = touchStartX - touchEndX;

    if (Math.abs(swipeDistance) >= swipeThreshold) {
      swipeDistance > 0 ? goNext() : goPrev();
    }

    setTouchStartX(null);
  };

  return (
    <section
      id="recenze"
      data-testid="reviews-section"
      className="relative bg-slate-50 py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <div className="flex items-center gap-3 mb-3">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-brand-orange">
              Recenze
            </p>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy px-3 py-1 text-xs font-bold text-white">
              <Star className="h-3.5 w-3.5 fill-brand-orange text-brand-orange" />
              5,0 / 5,0
            </span>
          </div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl text-brand-navy tracking-tight leading-none">
            Co říkají naši zákazníci
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            Recenze od lidí, kteří u nás řešili klíče, autoklíče nebo zámky
            v Ostravě.
          </p>
        </Reveal>

        <Reveal className="mt-10 md:hidden">
          <div
            className="touch-pan-y select-none"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <ReviewCard review={activeReview} />
            <div className="mt-5 flex items-center justify-between">
              <button
                type="button"
                onClick={goPrev}
                aria-label="Předchozí recenze"
                className="grid h-11 w-11 place-items-center rounded-full bg-brand-navy text-white shadow-lg shadow-brand-navy/20 transition-colors hover:bg-brand-navy2"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div
                className="flex items-center gap-2"
                aria-label={"Recenze " + (activeIndex + 1) + " z " + REVIEWS.length}
              >
                {REVIEWS.map((review, index) => (
                  <button
                    key={review.name}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    aria-label={"Zobrazit recenzi " + (index + 1)}
                    className={
                      "h-2.5 rounded-full transition-all " +
                      (index === activeIndex
                        ? "w-7 bg-brand-orange"
                        : "w-2.5 bg-slate-300")
                    }
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={goNext}
                aria-label="Další recenze"
                className="grid h-11 w-11 place-items-center rounded-full bg-brand-navy text-white shadow-lg shadow-brand-navy/20 transition-colors hover:bg-brand-navy2"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>

        <RevealGroup className="mt-14 hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {REVIEWS.map((r) => (
            <RevealItem key={r.name}>
              <ReviewCard review={r} />
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
};

export default Reviews;
